import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Pagination,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getDailyStatsSummary, getDailyStatsForDay } from '../services/api';
import type { DailyStatsSummaryResponse, DailyStatsItem, DailyAMIItem } from '../types/api';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

export default function DailyStats() {
  const [summaryData, setSummaryData] = useState<DailyStatsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [statsType, setStatsType] = useState<'added' | 'scanned' | 'failed'>('scanned');
  const [dailyAmis, setDailyAmis] = useState<DailyAMIItem[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(1000);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  type SortableField = 'imageId' | 'name' | 'owner' | 'region' | 'date';
  const [sortField, setSortField] = useState<SortableField>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Fetch summary data on mount
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getDailyStatsSummary();
        if (response.error) {
          setError(response.error);
        } else if (response.data && response.data.stats) {
          setSummaryData(response.data);
          // Set today as default selected date
          if (response.data.stats.length > 0) {
            const latestDate = response.data.stats[response.data.stats.length - 1].date;
            setSelectedDate(latestDate);
          }
        } else {
          setError('Invalid data format received from server');
        }
      } catch (err) {
        setError('Failed to fetch daily statistics');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Fetch daily AMIs when date, type, or page changes
  useEffect(() => {
    if (!selectedDate) return;

    const fetchDailyAmis = async () => {
      setDailyLoading(true);
      setDailyError(null);

      try {
        const offset = (page - 1) * pageSize;
        const response = await getDailyStatsForDay(selectedDate, statsType, pageSize, offset);
        if (response.error) {
          setDailyError(response.error);
          setDailyAmis([]);
          setTotalCount(0);
        } else {
          // Extract items and pagination metadata
          setDailyAmis(response.data.items || []);
          setTotalCount(response.data.pagination.total);
        }
      } catch (err) {
        setDailyError('Failed to fetch AMIs for selected date');
        setDailyAmis([]);
        setTotalCount(0);
        console.error(err);
      } finally {
        setDailyLoading(false);
      }
    };

    fetchDailyAmis();
  }, [selectedDate, statsType, page, pageSize]);

  // Reset page to 1 when date or type changes
  useEffect(() => {
    setPage(1);
  }, [selectedDate, statsType]);

  const handleDateChange = (event: SelectChangeEvent) => {
    setSelectedDate(event.target.value);
  };

  const handleStatsTypeChange = (_: React.MouseEvent<HTMLElement>, newType: 'added' | 'scanned' | 'failed' | null) => {
    if (newType !== null) {
      setStatsType(newType);
    }
  };

  // Format date in UTC (date only)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  // Format detailed timestamp showing both UTC and local time
  const formatDetailedDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';

      // Format in UTC
      const utcTime = date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'UTC',
        timeZoneName: 'short'
      });

      // Format in user's local timezone
      const localTime = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      return `${utcTime} (${localTime})`;
    } catch {
      return 'Invalid date';
    }
  };

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort the AMI list
  const sortedDailyAmis = [...dailyAmis].sort((a, b) => {
    let aValue: string | number = '';
    let bValue: string | number = '';

    switch (sortField) {
      case 'imageId':
        aValue = a.imageId || '';
        bValue = b.imageId || '';
        break;
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'owner':
        aValue = a.owner || '';
        bValue = b.owner || '';
        break;
      case 'region':
        aValue = a.region || '';
        bValue = b.region || '';
        break;
      case 'date': {
        // Get the appropriate date based on statsType
        const aDate = statsType === 'added' ? a.addedDate : a.scanDate;
        const bDate = statsType === 'added' ? b.addedDate : b.scanDate;
        aValue = aDate ? new Date(aDate).getTime() : 0;
        bValue = bDate ? new Date(bDate).getTime() : 0;
        break;
      }
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return sortDirection === 'asc' ? comparison : -comparison;
    } else {
      const comparison = (aValue as number) - (bValue as number);
      return sortDirection === 'asc' ? comparison : -comparison;
    }
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!summaryData || summaryData.stats.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">No daily statistics data available.</Alert>
      </Container>
    );
  }

  // Filter to show only last 14 days for charts
  const last14DaysStats = summaryData.stats.slice(-14);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
        <Navbar />
      </Box>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Daily AMI Statistics
        </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Showing the last 2 weeks of AMI scans, scan failures, and additions to database. Data grouped by UTC date. Timestamps shown in both UTC and your local timezone.
      </Typography>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* AMIs Added Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AMIs Added per Day
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={last14DaysStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                />
                <YAxis />
                <Tooltip labelFormatter={(date) => formatDate(date as string)} />
                <Legend />
                <Line type="monotone" dataKey="amisAdded" stroke="#1976d2" name="AMIs Added" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* AMIs Scanned Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AMIs Scanned per Day
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={last14DaysStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                />
                <YAxis />
                <Tooltip labelFormatter={(date) => formatDate(date as string)} />
                <Legend />
                <Line type="monotone" dataKey="amisScanned" stroke="#2e7d32" name="AMIs Scanned" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* AMI Scan Failures Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AMI Scan Failures per Day
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={last14DaysStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                />
                <YAxis />
                <Tooltip labelFormatter={(date) => formatDate(date as string)} />
                <Legend />
                <Line type="monotone" dataKey="amisScanFailures" stroke="#d32f2f" name="AMI Scan Failures" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Day Selector and AMI Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          View AMIs for a Specific Day
        </Typography>

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Select Date</InputLabel>
            <Select value={selectedDate} label="Select Date" onChange={handleDateChange}>
              {summaryData.stats.slice().reverse().map((stat: DailyStatsItem) => (
                <MenuItem key={stat.date} value={stat.date}>
                  {formatDate(stat.date)} ({stat.amisAdded} added, {stat.amisScanned} scanned, {stat.amisScanFailures} failed)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup value={statsType} exclusive onChange={handleStatsTypeChange} aria-label="stats type">
            <ToggleButton value="scanned" aria-label="scanned">
              AMIs Scanned
            </ToggleButton>
            <ToggleButton value="failed" aria-label="failed">
              Scan Failures
            </ToggleButton>
            <ToggleButton value="added" aria-label="added">
              AMIs Added to DB
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* AMI Table */}
        {dailyLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : dailyError ? (
          <Alert severity="error">{dailyError}</Alert>
        ) : dailyAmis.length === 0 ? (
          <Alert severity="info">No AMIs found for {formatDate(selectedDate)} ({statsType}).</Alert>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              {totalCount} total AMI{totalCount !== 1 ? 's' : ''}{' '}
              {statsType === 'scanned' ? 'scanned on' : statsType === 'failed' ? 'failed on' : 'added on'}{' '}
              {formatDate(selectedDate)}
              {totalCount > pageSize && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)})
                </Typography>
              )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'imageId'}
                        direction={sortField === 'imageId' ? sortDirection : 'asc'}
                        onClick={() => handleSort('imageId')}
                      >
                        AMI ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortDirection : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'owner'}
                        direction={sortField === 'owner' ? sortDirection : 'asc'}
                        onClick={() => handleSort('owner')}
                      >
                        Account ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'region'}
                        direction={sortField === 'region' ? sortDirection : 'asc'}
                        onClick={() => handleSort('region')}
                      >
                        Region
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'date'}
                        direction={sortField === 'date' ? sortDirection : 'asc'}
                        onClick={() => handleSort('date')}
                      >
                        Timestamp
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDailyAmis.map((ami) => (
                    <TableRow key={ami.imageId} hover>
                      <TableCell>
                        <Link to={`/image/${ami.imageId}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                          {ami.imageId}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ami.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Link to={`/account/${ami.owner}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                          {ami.owner}
                        </Link>
                      </TableCell>
                      <TableCell>{ami.region || 'N/A'}</TableCell>
                      <TableCell>
                        {statsType === 'added' && ami.addedDate ? formatDetailedDate(ami.addedDate) :
                         (statsType === 'scanned' || statsType === 'failed') && ami.scanDate ? formatDetailedDate(ami.scanDate) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {totalCount > pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Last Updated */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        Data last updated: {new Date(summaryData.lastUpdated).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'UTC',
          timeZoneName: 'short'
        })}
      </Typography>
    </Container>
    </>
  );
}
